import { useMemo, useCallback, useState } from 'react'
import type { JSX } from 'react'
import { Outlet, useParams, useNavigate, useMatch } from 'react-router-dom'

import {
  CornerDownRightIcon,
  DropdownMenu,
  EllipsisIcon,
  Flex,
  FolderIcon,
  IconButton,
  LockIcon,
  PageHeader,
  PageLayout,
  PencilIcon,
  RssIcon,
  toast,
  useIsMobileScreenSize,
} from '@buffer-mono/popcorn'

import {
  useAccount,
  useHasOrganizationAction,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { range } from '~publish/helpers/range'
import { isOfType } from '~publish/helpers/typeGuards'
import { isFreeUser } from '~publish/helpers/user'
import { usePageTitle } from '~publish/hooks/usePageTitle'

import { Limits } from '../../constants/constants'
import { useDeleteFeed } from '../../hooks/useDeleteFeed'
import { useFeedCollectionsForDropdown } from '../../hooks/useFeedCollectionsForDropdown'
import { useFeedDetails } from '../../hooks/useFeedDetails'
import { useFeedItems } from '../../hooks/useFeedItems'
import { useMoveFeed } from '../../hooks/useMoveFeed'
import { useRefreshFeed } from '../../hooks/useRefreshFeed'
import { useUpdateFeed } from '../../hooks/useUpdateFeed'
import {
  FeedArticleCardSkeleton,
  FeedArticleLoadFailure,
} from '../FeedArticleList/FeedArticleCard'
import { FeedArticleList } from '../FeedArticleList/FeedArticleList'
import { HardLimitReachedDialog } from '../HardLimitReachedDialog/HardLimitReachedDialog'
import { ListHeaderIcon } from '../ListHeader/ListHeaderIcon'
import { ListHeaderTitleEditor } from '../ListHeaderV2/ListHeaderTitleEditor'
import { ListHeaderTitleV2 } from '../ListHeaderV2/ListHeaderTitleV2'
import { RefreshAction } from '../RefreshAction'
import { DeleteDropdownMenuItem } from '../DeleteConfirmationDialog/DeleteConfirmationDialog'

import styles from './FeedListV2.module.css'

export const FeedListV2 = (): JSX.Element | null => {
  const { feedId } = useParams<{ feedId: string }>()
  const organizationId = useOrganizationId()
  const navigate = useNavigate()
  const feedMatch = useMatch('/create/feeds/:feedId')
  const isMobileScreenSize = useIsMobileScreenSize()

  const { feed, error } = useFeedDetails({ feedId, organizationId })

  const [deleteFeed] = useDeleteFeed()
  const [moveFeed] = useMoveFeed()

  const { collections } = useFeedCollectionsForDropdown({ organizationId })
  const { account } = useAccount()
  const tier = isFreeUser(account) ? 'free' : 'paid'
  const canManageFeeds = useHasOrganizationAction('manageFeeds')
  const [isRenaming, setIsRenaming] = useState(false)

  const moveToCollectionsList = useMemo(() => {
    return collections.map(
      (collection: {
        id: string
        name: string
        feeds: Array<{ id: string }>
      }) => ({
        id: collection.id,
        name: collection.name,
        limitReached:
          (collection.feeds?.length ?? 0) >= Limits.feeds[tier].perCollection,
      }),
    )
  }, [collections, tier])

  const handleMoveFeed = useCallback(
    async (targetFeedId: string, collectionId?: string) => {
      if (!organizationId) return

      const { error: moveFeedError } = await moveFeed({
        variables: {
          input: {
            id: targetFeedId,
            organizationId,
            collectionId,
          },
        },
      })

      if (moveFeedError) {
        toast.error('Failed to move feed')
      } else {
        toast.success('Feed moved successfully')
      }
    },
    [moveFeed, organizationId],
  )

  const handleDeleteFeed = useCallback(
    async (targetFeedId: string) => {
      if (!organizationId) return

      const { error: deleteFeedError } = await deleteFeed({
        variables: {
          input: {
            id: targetFeedId,
            organizationId,
          },
        },
      })

      if (deleteFeedError) {
        toast.error('Failed to delete feed')
        return
      }

      toast.success('Feed deleted successfully')

      // Only redirect if the deleted feed is the active one
      if (feedMatch?.params.feedId === targetFeedId) {
        navigate('/create/feeds')
      }
    },
    [deleteFeed, organizationId, navigate, feedMatch?.params.feedId],
  )

  const [updateFeed] = useUpdateFeed()
  const handleRenameFeed = useCallback(
    async (newName: string): Promise<void> => {
      if (!organizationId || !feed?.id) return

      const { error: updateFeedError } = await updateFeed({
        variables: {
          input: {
            id: feed.id,
            organizationId,
            name: newName,
          },
        },
      })

      if (updateFeedError) {
        toast.error('Failed to rename feed')
      } else {
        toast.success('Feed renamed successfully')
      }
      setIsRenaming(false)
    },
    [updateFeed, organizationId, feed?.id],
  )

  const [refreshFeed] = useRefreshFeed()
  const handleRefreshFeed = useCallback(async (): Promise<void> => {
    if (!organizationId || !feed?.id) {
      return
    }
    try {
      const { error: refreshFeedError } = await refreshFeed({
        variables: {
          input: {
            feedId: feed.id,
            organizationId,
          },
        },
      })
      if (!refreshFeedError) {
        toast.success('Your feed has been refreshed!')
      } else {
        toast.error('Failed to refresh feed')
      }
    } catch {
      toast.error('Failed to refresh feed')
    }
  }, [feed?.id, organizationId, refreshFeed])

  const {
    loading: loadingFeedItems,
    items,
    fetchingMore,
    lastElementRef,
    error: feedItemsError,
  } = useFeedItems({
    organizationId,
    feedIds: [feedId ?? ''],
  })

  usePageTitle(feed?.name ? `${feed.name} (Feed)` : 'Feeds')

  const loadState = useMemo(() => {
    if (loadingFeedItems && !items?.length) {
      return { state: 'loading' } as const
    }
    if (error || feedItemsError) {
      return { state: 'error', error: error || feedItemsError } as const
    }
    if (items && items.length > 0) {
      return { state: 'success', items } as const
    }
    if (items && items.length === 0 && !loadingFeedItems) {
      return { state: 'success', items } as const
    }
    return { state: 'loading' } as const
  }, [error, feedItemsError, items, loadingFeedItems])

  const feedUrl = isOfType(
    feed?.connectionMetadata,
    'RSSFeedConnectionMetadata',
  )
    ? feed.connectionMetadata.url
    : undefined

  const lastUpdated = feed?.lastRefreshed ?? new Date().toISOString()

  return (
    <>
      <Outlet />
      <PageHeader separator={false} className={styles.narrowHeader}>
        <PageHeader.Row size="narrow">
          <Flex align="center" gap="sm">
            {isRenaming && feed ? (
              <ListHeaderTitleEditor
                defaultValue={feed.name}
                icon={
                  <ListHeaderIcon
                    title={feed.name}
                    isCollection={false}
                    domain={feedUrl}
                    isLoading={false}
                  />
                }
                onUpdate={handleRenameFeed}
                onClose={(): void => setIsRenaming(false)}
              />
            ) : (
              <ListHeaderTitleV2
                title={feed?.name ?? ''}
                isCollection={false}
                domain={feedUrl}
                isLoading={!feed}
              />
            )}
            {!isMobileScreenSize && feed && canManageFeeds && !isRenaming && (
              <DropdownMenu>
                <DropdownMenu.Trigger>
                  <IconButton
                    label="Feed actions"
                    size="medium"
                    variant="tertiary"
                  >
                    <EllipsisIcon />
                  </IconButton>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="start">
                  <DropdownMenu.Sub>
                    <DropdownMenu.SubTrigger>
                      <CornerDownRightIcon />
                      Move to
                    </DropdownMenu.SubTrigger>
                    <DropdownMenu.SubMenu>
                      <DropdownMenu.CheckboxItem
                        key="no-collection"
                        checked={!feed.collection}
                        onSelect={(event): void => {
                          if (feed.collection) {
                            void handleMoveFeed(feed.id)
                          } else {
                            event.preventDefault()
                          }
                        }}
                      >
                        <RssIcon />
                        Feeds
                      </DropdownMenu.CheckboxItem>
                      {moveToCollectionsList.map(
                        (collection: {
                          id: string
                          name: string
                          limitReached: boolean
                        }) => (
                          <HardLimitReachedDialog
                            key={`moveToCollection-hardLimitReached-${collection.id}`}
                            enabled={
                              collection.limitReached &&
                              collection.id !== feed.collection?.id
                            }
                            type="feedspercollection"
                            tier={tier}
                          >
                            <DropdownMenu.CheckboxItem
                              key={collection.id}
                              checked={collection.id === feed.collection?.id}
                              onSelect={(event): void => {
                                if (
                                  !collection.limitReached ||
                                  collection.id === feed.collection?.id
                                ) {
                                  void handleMoveFeed(feed.id, collection.id)
                                } else {
                                  event.preventDefault()
                                }
                              }}
                            >
                              <FolderIcon />
                              {collection.name}
                              {collection.limitReached &&
                                collection.id !== feed.collection?.id && (
                                  <LockIcon />
                                )}
                            </DropdownMenu.CheckboxItem>
                          </HardLimitReachedDialog>
                        ),
                      )}
                    </DropdownMenu.SubMenu>
                  </DropdownMenu.Sub>
                  <DropdownMenu.Item onClick={(): void => setIsRenaming(true)}>
                    <PencilIcon />
                    Rename
                  </DropdownMenu.Item>
                  <DeleteDropdownMenuItem
                    mode="feed"
                    onConfirm={(): void => {
                      void handleDeleteFeed(feed.id)
                    }}
                  />
                </DropdownMenu.Content>
              </DropdownMenu>
            )}
          </Flex>
          <PageHeader.Actions>
            <RefreshAction
              lastUpdated={lastUpdated}
              onRefresh={handleRefreshFeed}
            />
          </PageHeader.Actions>
        </PageHeader.Row>
      </PageHeader>
      <PageLayout.Container size="narrow">
        {loadState.state === 'error' && <FeedArticleLoadFailure />}
        {loadState.state === 'loading' && (
          <div aria-live="polite" aria-label="Loading feed items">
            {range(20).map((index) => (
              <FeedArticleCardSkeleton key={index} />
            ))}
          </div>
        )}
        {loadState.state === 'success' && (
          <FeedArticleList
            items={loadState.items}
            fetchingMore={fetchingMore}
            lastElementRef={lastElementRef}
          />
        )}
      </PageLayout.Container>
    </>
  )
}
